import React from 'react';
import { Stack } from '@planne-software/uni/mui/material';
import { IconPhoto, IconCheck, IconInfoCircle } from '@planne-software/uni/icons';
import { Alert, Input } from '@planne-software/uni';
import { QuantitySelector } from '../../QuantitySelector';
import {
  AdditionalCardRoot,
  AvatarBorder,
  AvatarWrap,
  CardHeaderRow,
  CardMain,
  CheckboxOuter,
  DescriptionDivider,
  DescriptionSection,
  DescriptionText,
  MetaText,
  StatusText,
  TextColumn,
  TitleText,
} from './styles';
import { useAdditionalRow } from './useAdditionalRow';
import type { Additional } from '@src/core/buyerApiClient';
import type { Translate } from '@src/i18n/useTranslate';

export type AdditionalRowProps = {
  additional: Additional;
  widgetId: string;
  productId: string;
  i18n: Translate['i18n'];
};

export const AdditionalRow = (props: AdditionalRowProps) => {
  const {
    selected,
    imageUrl,
    title,
    priceLabel,
    statusText,
    showAvailability,
    showCheckbox,
    onToggle,
    descriptionText,
    showQuantityControls,
    quantity,
    quantityLabel,
    decrementAriaLabel,
    incrementAriaLabel,
    onDecrement,
    onIncrement,
    incrementDisabled,
    quantityError,
    showInput,
    input,
    inputPlaceholder,
    onInputChange,
  } = useAdditionalRow(props);

  return (
    <AdditionalCardRoot selected={selected}>
      <CardHeaderRow>
        <CardMain>
          <AvatarWrap>
            {imageUrl ? <img src={imageUrl} alt='' /> : <IconPhoto size={24} />}
            <AvatarBorder />
          </AvatarWrap>
          <TextColumn>
            <TitleText>{title}</TitleText>
            <Stack spacing={0}>
              <MetaText>{priceLabel}</MetaText>
              {showAvailability ? <StatusText>{statusText}</StatusText> : null}
            </Stack>
          </TextColumn>
        </CardMain>
        {showCheckbox ? (
          <CheckboxOuter role='checkbox' aria-checked={selected} selected={selected} onClick={onToggle}>
            {selected ? <IconCheck size={14} stroke={2.5} /> : null}
          </CheckboxOuter>
        ) : null}
      </CardHeaderRow>

      {descriptionText ? (
        <DescriptionSection>
          <DescriptionDivider aria-hidden />
          <DescriptionText>{descriptionText}</DescriptionText>
        </DescriptionSection>
      ) : null}

      {showQuantityControls ? (
        <>
          <QuantitySelector
            value={quantity}
            label={quantityLabel}
            decrementAriaLabel={decrementAriaLabel}
            incrementAriaLabel={incrementAriaLabel}
            onDecrement={onDecrement}
            onIncrement={onIncrement}
            incrementDisabled={incrementDisabled}
          />
          {quantityError ? (
            <Alert
              severity='error'
              canClose={false}
              title={quantityError}
              startIcon={<IconInfoCircle size={24} stroke={2} />}
              sx={{ marginTop: 0, width: '100%' }}
            />
          ) : null}
        </>
      ) : null}

      {showInput ? (
        <Input value={input} onChange={(e) => onInputChange(e.target.value)} placeholder={inputPlaceholder} />
      ) : null}
    </AdditionalCardRoot>
  );
};
